import React from 'react'
import { Box, Paper, CircularProgress, Typography } from '@material-ui/core'
import { useHistory, useParams, generatePath } from 'react-router-dom'
import { useQuery, useMutation, queryCache } from 'react-query'
import { useSnackbar } from 'notistack'
import Button from 'UI/Button/ButtonWithPreloader'
import { getArticle, getSections, updateArticle } from 'services/knowledgeBase'
import KnowledgeBaseForm from 'components/KnowledgeBaseForm'
import { IArticleForm } from 'typescript/interfaces/knowledgeBase'
import { KB_LIST_URL, KB_VIEW_URL } from 'constants/routes'
import { SOMETHING_WENT_WRONG } from 'constants/errors'

const KnowledgeBaseEdit = () => {
  const history = useHistory()
  const { id } = useParams<{ id: string }>()
  const articleId = Number(id)
  const { enqueueSnackbar } = useSnackbar()

  const { data: sections } = useQuery('kb-sections', getSections)
  const { data: article, isLoading: isLoadingArticle, isError } = useQuery(['kb-article', articleId], () => getArticle(articleId))

  const [updateArticleMut, { isLoading }] = useMutation(updateArticle, {
    onSuccess: () => {
      queryCache.invalidateQueries(['kb-article', articleId])
      queryCache.invalidateQueries('kb-articles')
      enqueueSnackbar('Page successfully updated')
      history.push(generatePath(KB_VIEW_URL, { id: articleId }))
    },
    onError: () => enqueueSnackbar(SOMETHING_WENT_WRONG),
  })

  if (isLoadingArticle) {
    return (
      <Box display="flex" justifyContent="center" p="4rem">
        <CircularProgress color="primary" />
      </Box>
    )
  }

  if (isError || !article) {
    return (
      <Box component={Paper} p="3rem" mb="2rem" display="flex" flexDirection="column" alignItems="center">
        <Typography variant="h3" color="textSecondary">
          This page could not be loaded. It may have been deleted or is temporarily unavailable.
        </Typography>
        <Box mt="2rem">
          <Button color="primary" variant="contained" onClick={() => history.push(KB_LIST_URL)}>
            Back to Knowledge Base
          </Button>
        </Box>
      </Box>
    )
  }

  const initValues: IArticleForm = {
    title: article.data.title,
    sectionId: article.data.sectionId,
    bodyHtml: article.data.bodyHtml,
    isPinned: article.data.isPinned,
  }

  return (
    <Box component={Paper} p="3rem" mb="2rem">
      <Box mb="1.5rem">
        <Button variant="text" color="primary" onClick={() => history.goBack()}>
          ← Back
        </Button>
      </Box>
      <KnowledgeBaseForm
        heading="Edit Page"
        sections={sections ? sections.data : []}
        initValues={initValues}
        loading={isLoading}
        onSubmit={(values) => updateArticleMut({ id: articleId, values })}
        onCancel={() => history.push(generatePath(KB_VIEW_URL, { id: articleId }))}
      />
    </Box>
  )
}

export default KnowledgeBaseEdit
